import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus } from 'lucide-react';
import { equipmentAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const AddEquipmentDialog = ({ onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    subcategory: '',
    quantity: '',
    available: '',
    price: '',
    serialNumber: '',
    status: 'available',
    location: '',
    condition: 'good'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await equipmentAPI.create({
        ...formData,
        quantity: parseInt(formData.quantity),
        available: parseInt(formData.available),
        price: parseFloat(formData.price)
      });
      
      toast({
        title: "Gelukt",
        description: "Apparatuur succesvol toegevoegd"
      });
      
      setOpen(false);
      setFormData({
        name: '',
        category: '',
    subcategory: '',
        quantity: '',
        available: '',
        price: '',
        serialNumber: '',
        status: 'available',
        location: '',
        condition: 'good'
      });
      
      if (onSuccess) onSuccess();
    } catch (error) {
      toast({
        title: "Fout",
        description: "Kan apparatuur niet toevoegen",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg">
          <Plus className="w-4 h-4 mr-2" />
          Apparatuur Toevoegen
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nieuwe Apparatuur Toevoegen</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Apparatuur Naam *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="category">Hoofdcategorie *</Label>
              <Input
                id="category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="bijv. Geluid, Licht, Video"
                required
              />
            </div>
            <div>
              <Label htmlFor="subcategory">Subcategorie</Label>
              <Input
                id="subcategory"
                value={formData.subcategory}
                onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                placeholder="bijv. Speakers, Moving Heads"
              />
            </div>
            <div>
              <Label htmlFor="quantity">Totale Hoeveelheid *</Label>
              <Input
                id="quantity"
                type="number"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="available">Beschikbare Hoeveelheid *</Label>
              <Input
                id="available"
                type="number"
                value={formData.available}
                onChange={(e) => setFormData({ ...formData, available: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="price">Prijs per Dag (€) *</Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="serialNumber">Serienummer *</Label>
              <Input
                id="serialNumber"
                value={formData.serialNumber}
                onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="location">Locatie *</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="bijv. Magazijn A"
                required
              />
            </div>
            <div>
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="available">Beschikbaar</SelectItem>
                  <SelectItem value="rented">Verhuurd</SelectItem>
                  <SelectItem value="maintenance">Onderhoud</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="condition">Conditie</Label>
              <Select value={formData.condition} onValueChange={(value) => setFormData({ ...formData, condition: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="excellent">Uitstekend</SelectItem>
                  <SelectItem value="good">Goed</SelectItem>
                  <SelectItem value="fair">Redelijk</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Annuleren
            </Button>
            <Button type="submit" disabled={loading} className="bg-gradient-to-r from-blue-600 to-purple-600">
              {loading ? 'Toevoegen...' : 'Apparatuur Toevoegen'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddEquipmentDialog;
